import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AdministrationApiContextType } from '@/types/util.types';
import { useAuth } from './useAuth';
import { EklipxResearchUser, EntityInformation, User } from '@/types/admin';
import { ADMIN_API_URL, ADMIN_ENDPOINTS } from '@/constants/constants';

export const AdminApiContext = createContext<AdministrationApiContextType | null>(null);
export const useAdminApi = () => useContext(AdminApiContext);

export const AdminApiProvider = ({ children }: { children: React.ReactNode }) => {
  console.log('👉 Running useAdminApi hook');
  const { getAccessTokenOrRefresh } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [entityInformation, setEntity] = useState<EntityInformation>();
  const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
  const [safelistedDomains, setSafelistedDomains] = useState<string[]>([]);

  const getUsers = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
      if (token === null) return;

      const url = ADMIN_ENDPOINTS.getUsers;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setUsers(data.map((x: User) => new EklipxResearchUser(x)));
    } catch (error) {
      console.error('Error getting users:', error);
    }
  }, [getAccessTokenOrRefresh]);

  const getEntityInformation = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
      const url = ADMIN_ENDPOINTS.getEntityInformation;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setEntity(data);
    } catch (error) {
      console.error('Error getting entity:', error);
    }
  }, [getAccessTokenOrRefresh]);

  const getAllowedRoles = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
      const url = ADMIN_ENDPOINTS.getAllowedRoles;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setAllowedRoles(data);
    } catch (error) {
      console.error('Error getting allowed roles:', error);
    }
  }, [getAccessTokenOrRefresh]);

  const getSafelistedDomains = useCallback(async () => {
    try {
      const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
      const url = ADMIN_ENDPOINTS.getSafelistedDomains;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setSafelistedDomains(data);
    } catch (error) {
      console.error('Error getting safelisted domains:', error);
    }
  }, [getAccessTokenOrRefresh]);

  const updateEntityInformation = useCallback(
    async (entityToUpdate: EntityInformation) => {
      try {
        const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
        const url = ADMIN_ENDPOINTS.updateEntityInformation;
        await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(entityToUpdate),
        });
      } catch (error) {
        console.error('Error updating entity:', error);
      }
    },
    [getAccessTokenOrRefresh]
  );

  const inviteUserToEntity = useCallback(
    async (email: string, firstName: string, lastName: string, roles: string[]) => {
      try {
        const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
        const url = ADMIN_ENDPOINTS.addUser(email);
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ firstName, lastName, roles }),
        });
      } catch (error) {
        console.error('Error inviting user:', error);
      }
    },
    [getAccessTokenOrRefresh]
  );

  const inviteUserToEklipx = useCallback(
    async (email: string, firstName: string, lastName: string) => {
      try {
        const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
        const url = ADMIN_ENDPOINTS.invite;
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, firstName, lastName }),
        });
      } catch (error) {
        console.error('Error inviting user:', error);
      }
    },
    [getAccessTokenOrRefresh]
  );

  const removeUserFromEntity = useCallback(
    async (email: string) => {
      try {
        const token = await getAccessTokenOrRefresh(ADMIN_API_URL);
        const url = ADMIN_ENDPOINTS.deleteUser(email);
        await fetch(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        await getUsers();
      } catch (error) {
        console.error('Error removing user:', error);
      }
    },
    [getAccessTokenOrRefresh, getUsers]
  );

  useEffect(() => {
    getUsers();
    getEntityInformation();
    getAllowedRoles();
    getSafelistedDomains();
  }, [getAccessTokenOrRefresh, getUsers, getEntityInformation, getAllowedRoles, getSafelistedDomains]);

  return (
    <AdminApiContext.Provider
      value={{
        users,
        entityInformation,
        allowedRoles,
        safelistedDomains,
        updateEntityInformation,
        inviteUserToEntity,
        inviteUserToEklipx,
        removeUserFromEntity,
      }}
    >
      {children}
    </AdminApiContext.Provider>
  );
};
